import Cocktail from 'lib/Cocktail';
import Alerts from 'lib/alerts';
import template from '../../templates/settings/CompanySetup.ejs';
import LocationModal from 'employer/views/LocationModal';
import FormView from 'commons/views/Form';
import BankAccountMixin from 'commons/viewMixins/BankAccount';
import modal from 'helpers/modal';
import loader from 'helpers/loader';
import companyTypes from './CompanyTypes';

import Address from 'newModels/address.js';
import CompanyLocation from 'newModels/company-location.js';
import Employees from 'newCollections/employees.js';

function showAddEmployeesPrompt() {
    modal.confirm({
        title:              'Success!',
        type:               'success',
        customClass:        'sweet-alert-success',
        cancelButtonText:   'Add Later',
        confirmButtonText:  'Add Now',
        text:               'Would you like to add employees?',

        callback: isConfirm => app.mainRouter.navigate(
            isConfirm ? 'employee/add' : 'settings/company', {trigger: true}
        )
    });
}

function showAddEmployeesPromptIfNotAdded() {
    new Employees(null, {company: app.company}).count()
        .then(count => {
            if (count === 0) {
                showAddEmployeesPrompt();
                return;
            }
        });
}

module.exports = Cocktail.mixin(FormView.extend({
    locationModal:    null,
    bankAccountModal: null,

    events: function() {
        return Object.assign({}, FormView.prototype.events(), {
            'change input.js-ext-primary-location':  'updatePrimaryLocation',
            'change select.js-ext-primary-location': 'updatePrimaryLocation',
            'click .js-iakaun-btn':                  'iakaunToggle',
            'click .js-add-location':                'newLocation',
            'click .js-change-company-logo':         'onChangeLogo',
            'click .js-remove-company-logo':         'onRemoveLogo',
            'change .js-change-company-logo-input':  'uploadLogo'
        });
    },

    initialize: function(options = {}) {
        this.options = Object.assign({
            template: template,
            locations: [],
            bankAccountOverrides: {
                isPayer: true,
                isPayee: false
            },
            templateExtra: {
                companyTypes,
            }
        }, options);

        if (!this.options.locations.getPrimary()) {
            this.options.locations.add({
                name: 'HQ',
                primary: true,
                companyId: this.model.id
            }, {silent: true});
        }

        this.options.templateExtra = Object.assign(
            {
                primaryLocation: this.options.locations.getPrimary(),
                locations: this.options.locations,
                states: Address.getStatesList()
            },
            this.options.templateExtra || {}
        );

        this.options.bankAccountOverrides = Object.assign(
            this.options.bankAccountOverrides || {}, {
                companyId: this.model.id
            }
        );

        this.locationModal = new LocationModal();

        // Clear validation errors that could have been produced by just
        // fetching the model. This view has weird validation checking on save
        // and if we don't do this the first time the user hits save some
        // errors might be missing in the UI
        this.model.validationError = false;

        return FormView.prototype.initialize.apply(this, [this.options]);
    },

    bindHandlers: function() {
        FormView.prototype.bindHandlers.apply(this, arguments);

        this.options.locations.on(
            'add remove change', this.renderLocations, this
        );
        this.options.locations.on('error', this.invalidLocation, this);
        this.options.locations.on('change', this.validLocation, this);

        this.locationModal.on('save', this.addLocation, this);
        this.locationModal.on('remove', this.removeLocation, this);

        return this;
    },

    unbindHandlers: function() {
        FormView.prototype.unbindHandlers.apply(this, arguments);

        this.options.locations.off(null, null, this);
        this.locationModal.off(null, null, this);

        return this;
    },

    render: function() {
        FormView.prototype.render.apply(this, arguments);

        this.renderLocations();
        this.markMandatoryFields((new Address()).constraints());

        this.$('[name=eNumber]').inputmask('99999999-99');

        return this;
    },

    cancel: function(ev) {
        ev.preventDefault();

        const $button    = this.$(ev.currentTarget);
        const stopLoader = loader.startFullScreen($button);
        this.model.fetch()
            .then(() => this.model.fetchBankAccount())
            .then(() => this.options.locations.fetch())
            .then(() => this.options.locations.fetchAddresses())
            .then(() => this.options.locations.fetchEmployees())
            .then(() => this.render())
            .finally(() => stopLoader());
    },

    dirty: function() {
        return (
            this.model.dirty() ||
            this.options.locations.dirty() ||
            (
                this.model.getBankAccount() &&
                    this.model.getBankAccount().dirty()
            )
        );
    },

    warnMissingNumbers: function() {
        return new Promise((resolve) => {
            const missing = this.model.getMissingRegNumbers();

            if (missing.length === 0) {
                return resolve(true);
            }

            modal.warning({
                title: '',
                text: 'The following fields are required in ' +
                    'order to generate fully complete forms and documents ' +
                    'for your payroll: </br>' + missing.join(', </br>'),
                html:     true,
                callback: () => resolve(true)
            });
        });

    },

    save: function(ev) {
        ev.preventDefault();

        const $button    = this.$(ev.currentTarget);
        const primaryLoc = this.options.locations.getPrimary();
        let stopLoader = () => {};

        /* We could rely on error events, but since the form requires values
         * from two different models, we have to manually check both for
         * errors
         */
        let validationFailed = false;
        if (!primaryLoc.wasValid() || !primaryLoc.isValid()) {
            this.invalid(this.model, primaryLoc.validationError);
            validationFailed = true;
        }
        if (!this.model.wasValid() || !this.model.isValid()) {
            this.invalid(this.model, this.model.validationError);
            validationFailed = true;
        }
        if (validationFailed) {
            /* If either of the required models failed validation, bail out
             */
            return;
        }

        /* The locations are saved first because the company beforeSave
         * trigger will check them in order to determine if the company
         * is setup
         */
        this.warnMissingNumbers()
            .then(() => { stopLoader = loader.startFullScreen($button); })
            .then(() => {
                if (this.options.locations.dirty()) {
                    return this.options.locations.save();
                }
            })
            .then(() => {
                // if logo was removed, persist that through the
                // specific endpoint for the action
                if (
                    this.model.dirtyKeys().includes('logoUrl') &&
                    !this.model.get('logoUrl')
                ) {
                    return this.model.unsetLogo();
                }
            })
            .then(() => {
                if (
                    app.user.isAdmin() &&
                    this.model.dirtyKeys().includes('maxEmployeeCount')
                ) {
                    return this.model.setMaxEmployeeCount(
                        this.model.get('maxEmployeeCount')
                    );
                }
            })
            .then(() => this.model.save())
            // fetch again as changes in location can trigger changes in the
            // backend
            .then(() => this.model.fetch())
            .then(() => {
                //resetting the model binds handlers again and renders
                this.setModel(this.model);

                showAddEmployeesPromptIfNotAdded();

                modal.showAlertSuccess('Your changes have been saved');
            })
            .catch(err => { modal.error({text: err}); })
            .finally(() => stopLoader());
    },

    renderLocations: function() {
        const nonPrimary = this.options.locations.filter(location => {
            return location.get('primary') === false;
        });

        this.$('.js-location-list').html('');

        nonPrimary.forEach(location => {
            const address = location.getAddress();
            const $edit = $('<a href= "">').text('Edit');
            const stateName = address.getStateName();
            const addressString = [
                address.get('line1'),
                address.get('city'),
                stateName,
                address.get('postcode'),
                address.get('contactPhone')
            ].filter(s => !!s).join(', ');
            const $location = $('<li>').append(
                $('<strong>').text(location.get('name')),
                ` ${addressString}`,
                $edit
            );
            $edit.on('click', ev => {
                ev.preventDefault();

                this.locationModal.setModel(location);
                this.locationModal.render();
                this.locationModal.show();
            });

            this.$('.js-location-list').append($location);
        });

        if (nonPrimary.length) {
            this.$('.js-location-list').show();
            this.$('.js-location-list-none').hide();
        } else {
            this.$('.js-location-list').hide();
            this.$('.js-location-list-none').show();
        }
    },

    invalidLocation: function(model, errors) {
        if (model.get('primary') === true) {
            this.invalid(model, errors);
        }
    },

    validLocation: function(model, errors) {
        if (model.get('primary') === true) {
            this.valid(model, errors);
        }
    },

    updatePrimaryLocation: function(ev) {
        const $el = this.$(ev.currentTarget);
        const attr = $el.attr('name');
        const value = $el.val();
        const primaryLocation = this.options.locations.getPrimary();

        if (!primaryLocation.getAddress()) {
            return;
        }

        this.toggleError(attr);
        if (
            primaryLocation.validationError &&
            primaryLocation.validationError[attr]
        ) {
            delete primaryLocation.validationError[attr];
        }

        primaryLocation.getAddress().set({[attr]: value});
        if (!primaryLocation.wasValid()) {
            this.invalid(this.model, primaryLocation.validationError || {});
        }
    },

    newLocation: function() {
        let locationName = (this.model.get('companyName') || 'Location') +
            ' ' + (this.options.locations.length + 1);

        if (this.options.locations.length > 1) {
            const lastLocation = this.options.locations.last();
            const regex = /[0-9]+$/g;
            const m = regex.exec(lastLocation.get('name'));
            if (m && m.length > 0) {
                const newInt = parseInt(m[0]) + 1;
                locationName = (this.model.get('companyName') || 'Location') +
                    ' ' + newInt;
            }
        }

        const newCompanyLocation = new CompanyLocation({
            companyId: this.model.id,
            name: locationName,
            primary: false
        });
        newCompanyLocation.locations = this.options.locations;

        this.locationModal.setModel(newCompanyLocation);
        this.locationModal.render({addMode: true});
        this.locationModal.show();
    },

    addLocation: function(location) {
        if (location.isNew() && !this.options.locations.contains(location)) {
            this.options.locations.add(location);
        }
    },

    removeLocation: function(location) {
        if (this.options.locations.contains(location)) {
            this.options.locations.destroyLater(location);
            this.options.locations.remove(location);
        }
    },

    iakaunToggle: function(e) {
        var $iakaun = this.$('.js-iakaun');

        if ($iakaun.attr('type') === 'password') {
            $iakaun.attr('type', 'text');
            $(e.currentTarget).button('hide');
        } else {
            $iakaun.attr('type', 'password');
            $(e.currentTarget).button('reset');
        }
    },

    onChangeLogo: function() {
        this.$('.js-change-company-logo-input').click();
    },

    onRemoveLogo: function() {
        this.model.set('logoUrl', null);
        this.render();
    },

    uploadLogo: function(e) {
        const stopLoader = loader.startFullScreen();
        const selectedFile = this.$(e.currentTarget)[0].files[0];
        this.model.uploadLogo(selectedFile)
            .then(() => this.render())
            .catch(err => { Alerts.show('error', err); })
            .finally(() => stopLoader());
    }
}), BankAccountMixin);



// WEBPACK FOOTER //
// ./app/employer/views/settings/CompanySetup.js